import { type CSSProperties, memo } from 'react';

export interface RouteCircleProps {
  /** Grade text to display (e.g., "V3", "6a+"). Empty string shows no text. */
  grade: string;
  /** Hold color hex (inner fill) */
  holdColor: string;
  /** Tag color hex (outer ring). If omitted or undefined, no ring. */
  tagColor?: string | undefined;
  /** Diameter in pixels */
  size: number;
}

const RING_WIDTH = 4;

const getContrastingTextColor = (hex: string): string => {
  const color = hex.replace('#', '');
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return '#FFFFFF';
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#1F2937' : '#FFFFFF';
};

const isLightColor = (hex: string): boolean => {
  const color = hex.replace('#', '');
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return false;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.85;
};

const isDarkColor = (hex: string): boolean => {
  const color = hex.replace('#', '');
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return false;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.15;
};

export const RouteCircle = memo(function RouteCircle({
  grade,
  holdColor,
  tagColor,
  size,
}: RouteCircleProps) {
  const textColor = getContrastingTextColor(holdColor);
  const hasTagRing = !!tagColor;
  const fontSize = grade.length > 3 ? size * 0.3 : size * 0.38;

  // Determine if additional outline is needed for visibility
  // Light tags blend with light mode, dark tags blend with dark mode
  // Also check if hold color is very light (needs outline even without tag ring)
  const tagNeedsOutline = hasTagRing && (isLightColor(tagColor!) || isDarkColor(tagColor!));
  const holdNeedsOutline = isLightColor(holdColor) || isDarkColor(holdColor);

  // Always add a gray outline (matching map pins) plus subtle shadow for depth
  const getBoxShadow = (): string => {
    // All circles get the gray outline like map pins, plus depth shadow
    // Extra visibility outline for light/dark colors
    if (tagNeedsOutline || holdNeedsOutline) {
      return '0 0 0 1.5px rgba(100, 100, 100, 0.35), 0 1px 3px rgba(0, 0, 0, 0.1)';
    }
    return '0 0 0 1.5px rgba(100, 100, 100, 0.35), 0 1px 3px rgba(0, 0, 0, 0.08)';
  };

  const circleStyle: CSSProperties = {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '50%',
    backgroundColor: holdColor,
    borderWidth: hasTagRing ? `${RING_WIDTH}px` : '1px',
    borderStyle: 'solid',
    borderColor: hasTagRing
      ? tagColor
      : isLightColor(holdColor)
        ? 'rgba(0, 0, 0, 0.15)'
        : 'rgba(0, 0, 0, 0.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxSizing: 'border-box',
    // Add shadow for depth and outline for light/dark colors
    boxShadow: getBoxShadow(),
  };

  const textStyle: CSSProperties = {
    fontSize: `${fontSize}px`,
    fontWeight: 700,
    color: textColor,
    lineHeight: 1,
    letterSpacing: '-0.02em',
  };

  return (
    <div style={circleStyle} aria-hidden="true">
      {grade && <span style={textStyle}>{grade}</span>}
    </div>
  );
});

export default RouteCircle;
